/**
 * CodeEditor — Monaco-based code editor for Pine Script and Python strategies.
 *
 * Pine Script is not a built-in Monaco language; it uses Python syntax
 * highlighting as a close approximation (keywords, comments, strings).
 * A lightweight Pine Script token provider can be added later via
 * `monaco.languages.register` + `setMonarchTokensProvider`.
 */
import React, { useCallback, useRef } from "react";
import MonacoEditor from "@monaco-editor/react";

// @monaco-editor/react loads Monaco from CDN by default.
// No custom loader.config needed — the default CDN path works with CRA/Craco.

// ── Pine Script token provider (Monarch syntax) ──────────────────────────────
const PINESCRIPT_MONARCH = {
  defaultToken: "",
  keywords: [
    "strategy", "indicator", "library",
    "if", "else", "for", "while", "to", "by",
    "var", "varip", "import", "export",
    "true", "false", "na",
    "and", "or", "not",
    "switch", "type", "method",
  ],
  builtins: [
    "close", "open", "high", "low", "volume", "time",
    "bar_index", "last_bar_index", "barstate",
    "ta", "math", "str", "array", "matrix",
    "strategy", "alert", "label", "line", "box", "table",
    "color", "plot", "plotshape", "plotarrow", "fill", "bgcolor",
    "input", "request",
  ],
  tokenizer: {
    root: [
      [/\/\/.*$/, "comment"],
      [/"(?:[^"\\]|\\.)*"/, "string"],
      [/'(?:[^'\\]|\\.)*'/, "string"],
      [/\b(\d+(\.\d+)?)\b/, "number"],
      [
        /[a-zA-Z_][a-zA-Z0-9_]*/,
        {
          cases: {
            "@keywords": "keyword",
            "@builtins": "type",
            "@default": "identifier",
          },
        },
      ],
      [/[=><!+\-*/%&|^~]+/, "operator"],
      [/[{}()[\]]/, "delimiter"],
    ],
  },
};

let _pineScriptRegistered = false;

function ensurePineScriptLanguage(monaco) {
  if (_pineScriptRegistered) return;
  _pineScriptRegistered = true;
  monaco.languages.register({ id: "pinescript" });
  monaco.languages.setMonarchTokensProvider("pinescript", PINESCRIPT_MONARCH);
  monaco.languages.setLanguageConfiguration("pinescript", {
    comments: { lineComment: "//" },
    brackets: [
      ["{", "}"],
      ["[", "]"],
      ["(", ")"],
    ],
    autoClosingPairs: [
      { open: "{", close: "}" },
      { open: "[", close: "]" },
      { open: "(", close: ")" },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
    ],
  });
}

// ── Theme ────────────────────────────────────────────────────────────────────

const TRADOPRO_DARK_THEME = {
  base: "vs-dark",
  inherit: true,
  rules: [
    { token: "comment", foreground: "6a9955", fontStyle: "italic" },
    { token: "keyword", foreground: "c586c0" },
    { token: "type", foreground: "4ec9b0" },
    { token: "string", foreground: "ce9178" },
    { token: "number", foreground: "b5cea8" },
    { token: "operator", foreground: "d4d4d4" },
  ],
  colors: {
    "editor.background": "#18181b",          // zinc-900
    "editor.foreground": "#e4e4e7",          // zinc-200
    "editor.lineHighlightBackground": "#27272a", // zinc-800
    "editorLineNumber.foreground": "#52525b",
    "editorLineNumber.activeForeground": "#a1a1aa",
    "editor.selectionBackground": "#3f3f46",
    "editorIndentGuide.background": "#27272a",
  },
};

let _themeRegistered = false;

function ensureTheme(monaco) {
  if (_themeRegistered) return;
  _themeRegistered = true;
  monaco.editor.defineTheme("tradopro-dark", TRADOPRO_DARK_THEME);
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Props
 * ─────
 * value        string    — current code content
 * onChange     fn(str)   — called on every edit
 * language     string    — "pinescript" | "python" | "javascript" (default "python")
 * height       string    — CSS height of the editor (default "280px")
 * readOnly     bool      — disable editing (default false)
 * placeholder  string    — shown when value is empty (via Monaco overlay)
 * className    string    — wrapper div class names
 */
export default function CodeEditor({
  value = "",
  onChange,
  language = "python",
  height = "280px",
  readOnly = false,
  placeholder,
  className = "",
}) {
  const monacoRef = useRef(null);

  const handleBeforeMount = useCallback((monaco) => {
    monacoRef.current = monaco;
    ensureTheme(monaco);
    ensurePineScriptLanguage(monaco);
  }, []);

  const handleChange = useCallback(
    (newValue) => {
      if (onChange) onChange(newValue ?? "");
    },
    [onChange]
  );

  const resolvedLanguage =
    language === "pinescript" ? "pinescript" : language === "python" ? "python" : "plaintext";

  return (
    <div
      className={`rounded-md border border-zinc-700 overflow-hidden ${className}`}
      style={{ height }}
    >
      <MonacoEditor
        height={height}
        language={resolvedLanguage}
        value={value}
        theme="tradopro-dark"
        onChange={handleChange}
        beforeMount={handleBeforeMount}
        options={{
          readOnly,
          fontSize: 13,
          fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
          lineNumbers: "on",
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          wordWrap: "on",
          automaticLayout: true,
          tabSize: 4,
          insertSpaces: true,
          renderLineHighlight: "gutter",
          smoothScrolling: true,
          cursorBlinking: "smooth",
          padding: { top: 8, bottom: 8 },
          overviewRulerLanes: 0,
          glyphMargin: false,
          folding: true,
          placeholder: placeholder || undefined,
        }}
      />
    </div>
  );
}
